// components/SemanticModels.jsx

// React imports ******************************************************************
import React, { useCallback, useEffect, useMemo, useState } from "react";

// MUI imports ******************************************************************
import {
  Box, Button, Chip, CircularProgress, Container, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, IconButton, InputLabel, MenuItem,
  Select, Snackbar, Alert, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip, Typography, Switch, FormControlLabel
} from "@mui/material";
import { Add, DeleteOutline, Edit, Refresh } from "@mui/icons-material";

// Services ******************************************************************
import SemanticModelService from "../../../services/SemanticModelService";
import TablePaginationBar from "../../../components/common/TablePaginationBar";
import { useTablePagination } from "../../../hooks";

const MODEL_TYPES = [
  { value: "DATABASE", label: "Base de Datos" },
  { value: "API", label: "API" },
  { value: "FILE", label: "Archivo" },
  { value: "CONNECTION", label: "Conexión" }
];

const emptyForm = {
  semantic_model_id: "",
  user_account_id: "",
  user_accout_id: "",
  semantic_database_id: "",
  semantid_database_id: "",
  name: "",
  description: "",
  model_type: "DATABASE",
  is_public: false,
  is_deleted: false,
  is_active: true,
  version_id: "1",
  soft_delete: {
    deleted: false,
    deleted_at: null,
  },
};

const normalizeModel = (item = {}) => {
  const softDelete = item.soft_delete || item.SOFT_DELETE || {};

  return {
    semantic_model_id: item.semantic_model_id || item.semanticModelId || item.ID || item.id || "",
    user_account_id: item.user_account_id || item.user_accout_id || item.USER_ACCOUNT_ID || item.USER_ACCOUT_ID || "",
    user_accout_id: item.user_accout_id || item.USER_ACCOUT_ID || "",
    semantic_database_id: item.semantic_database_id || item.SEMANTIC_DATABASE_ID || "",
    semantid_database_id: item.semantid_database_id || item.SEMANTID_DATABASE_ID || "",
    name: item.name || item.MODEL_NAME || "",
    description: item.description || item.DESCRIPTION || "",
    model_type: item.model_type || item.MODEL_TYPE || "DATABASE",
    is_public: item.is_public ?? item.IS_PUBLIC ?? false,
    is_deleted: item.is_deleted ?? item.IS_DELETED ?? false,
    is_active: item.is_active ?? item.IS_ACTIVE ?? true,
    version_id: item.version_id || item.VERSION_ID || "1",
    soft_delete: {
      deleted: softDelete.deleted ?? softDelete.DELETED ?? false,
      deleted_at: softDelete.deleted_at || softDelete.DELETED_AT || null,
    },
  };
};

const SemanticModels = () => {
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Dialogo Crear/Editar
  const [openForm, setOpenForm] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // Dialogo eliminar
  const [openDelete, setOpenDelete] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  // El backend devuelve una página más `next_cursor`; la respuesta pasa
  // por adaptSemanticModelResponse, que hace spread, así que el cursor
  // llega intacto. Se aceptan también las formas viejas por si queda un
  // backend sin desplegar.
  const filasDe = (data) => (
    Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.semantic_models)
        ? data.semantic_models
        : Array.isArray(data)
          ? data
          : []
  );

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await SemanticModelService.list({ includeDeleted });
      setRows(filasDe(data).map(normalizeModel));
      setCursor(data?.next_cursor || null);
    } catch (e) {
      setError(e.message || "Error cargando modelos");
    } finally {
      setLoading(false);
    }
  };

  const loadMore = useCallback(async () => {
    if (!cursor || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const data = await SemanticModelService.list({ includeDeleted, cursor });
      setRows((prev) => [...prev, ...filasDe(data).map(normalizeModel)]);
      setCursor(data?.next_cursor || null);
    } catch (e) {
      setError(e.message || "Error cargando más modelos");
    } finally {
      setIsLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor, isLoadingMore, includeDeleted]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeDeleted]);

  const handleOpenCreate = () => {
    setIsEdit(false);
    setCurrentId(null);
    setForm({ ...emptyForm, soft_delete: { ...emptyForm.soft_delete } });
    setOpenForm(true);
  };

  const handleOpenEdit = async (row) => {
    try {
      setIsEdit(true);
      const targetId = row.semantic_model_id || row.ID;
      setCurrentId(targetId);
      const fresh = await SemanticModelService.getById(targetId);
      setForm({ ...emptyForm, ...normalizeModel(fresh) });
      setOpenForm(true);
    } catch (e) {
      setError(e.message || "No se pudo abrir la edición");
    }
  };

  const validate = useMemo(() => {
    const nameOk = form.name.trim().length >= 3;
    const typeOk = !form.model_type || MODEL_TYPES.some(t => t.value === form.model_type);
    return { nameOk, typeOk, valid: nameOk && typeOk };
  }, [form]);

  const handleSave = async () => {
    if (!validate.valid) return;
    try {
      setSaving(true);
      const payload = {
        name: form.name.trim(),
        description: (form.description || "").trim(),
        model_type: form.model_type,
        is_public: !!form.is_public,
        is_active: !!form.is_active,
        is_deleted: !!form.is_deleted,
        version_id: form.version_id || "1",
        soft_delete: {
          deleted: !!(form.soft_delete?.deleted),
          deleted_at: form.soft_delete?.deleted_at || null,
        },
      };

      if (form.semantic_database_id) {
        payload.semantic_database_id = form.semantic_database_id;
      }
      if (form.semantid_database_id) {
        payload.semantid_database_id = form.semantid_database_id;
      }
      if (form.user_account_id) {
        payload.user_account_id = form.user_account_id;
      }
      if (form.user_accout_id) {
        payload.user_accout_id = form.user_accout_id;
      }

      if (isEdit && currentId) {
        const updated = await SemanticModelService.update(currentId, payload);
        const normalized = normalizeModel({ ...(updated || {}), semantic_model_id: currentId });
        setRows(prev => prev.map(r => (
          r.semantic_model_id === currentId
            ? { ...r, ...normalized }
            : r
        )));
        setSuccess("Modelo actualizado");
      } else {
        const created = await SemanticModelService.create(payload);
        const normalized = normalizeModel(created);
        if (normalized.semantic_model_id) {
          setRows(prev => [...prev, { ...emptyForm, ...normalized }]);
        } else {
          await load();
        }
        setSuccess("Modelo creado");
      }
      setOpenForm(false);
      setForm({ ...emptyForm, soft_delete: { ...emptyForm.soft_delete } });
    } catch (e) {
      setError(e.message || "Error guardando el modelo");
    } finally {
      setSaving(false);
    }
  };

  const askDelete = (row) => {
    setToDelete(row);
    setOpenDelete(true);
  };

  const confirmSoftDelete = async () => {
    if (!toDelete) return;
    try {
      const targetId = toDelete.semantic_model_id || toDelete.ID;
      const updated = await SemanticModelService.softDelete(targetId);
      const normalized = normalizeModel({ ...(updated || {}), semantic_model_id: targetId });
      setRows(prev => prev.map(r => (
        r.semantic_model_id === targetId
          ? { ...r, ...normalized }
          : r
      )));
      setSuccess("Modelo marcado como eliminado (soft delete)");
    } catch (e) {
      setError(e.message || "Error al eliminar (soft)");
    } finally {
      setOpenDelete(false);
      setToDelete(null);
    }
  };

  const { pageRows, paginationProps } = useTablePagination(rows, {
    storageKey: 'semanticModels',
    resetDeps: [includeDeleted],
    hasMore: Boolean(cursor),
    isLoadingMore,
    onNeedMore: loadMore
  });

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5">Modelos Semánticos</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={includeDeleted}
                onChange={(e) => setIncludeDeleted(e.target.checked)}
              />
            }
            label="Ver eliminados"
          />
          <Tooltip title="Refrescar">
            <IconButton onClick={load}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<Add />} onClick={handleOpenCreate}>
            Nuevo Modelo
          </Button>
        </Stack>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table size="medium">
            <TableHead>
              <TableRow>
                <TableCell>Nombre del Modelo</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell align="center">Estado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, opacity: 0.7 }}>
                    No hay modelos para mostrar
                  </TableCell>
                </TableRow>
              ) : pageRows.map((row) => (
                <TableRow key={row.semantic_model_id || row.name} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{row.name}</TableCell>
                  <TableCell sx={{ maxWidth: 520 }}>
                    <Typography noWrap title={row.description || ""}>
                      {row.description || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {row.model_type ? (
                      <Chip
                        label={MODEL_TYPES.find(t => t.value === row.model_type)?.label || row.model_type}
                        variant="outlined"
                      />
                    ) : (
                      <Chip label="Sin tipo" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {(row.is_deleted || row.soft_delete?.deleted) ? (
                      <Chip label="Eliminado" color="error" size="small" />
                    ) : (
                      <Chip label="Activo" color="success" size="small" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar">
                      <span>
                        <IconButton
                          onClick={() => handleOpenEdit(row)}
                          disabled={!!(row.is_deleted || row.soft_delete?.deleted)}
                        >
                          <Edit />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Eliminar (soft)">
                      <span>
                        <IconButton
                          onClick={() => askDelete(row)}
                          disabled={!!(row.is_deleted || row.soft_delete?.deleted)}
                        >
                          <DeleteOutline />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && rows.length > 0 && (
        <TablePaginationBar variant="default" {...paginationProps} />
      )}

      {/* Dialogo Crear/Editar */}
      <Dialog open={openForm} onClose={() => !saving && setOpenForm(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isEdit ? "Editar Modelo" : "Nuevo Modelo"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nombre del Modelo"
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              required
              error={!validate.nameOk && form.name.trim().length > 0}
              helperText={!validate.nameOk ? "Mínimo 3 caracteres" : " "}
            />
            <TextField
              label="Descripción"
              multiline
              minRows={2}
              value={form.description}
              onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
            />
            <FormControl fullWidth>
              <InputLabel>Tipo de Modelo</InputLabel>
              <Select
                label="Tipo de Modelo"
                value={form.model_type}
                onChange={(e) => setForm(prev => ({ ...prev, model_type: e.target.value }))}
              >
                {MODEL_TYPES.map(mt => (
                  <MenuItem key={mt.value} value={mt.value}>{mt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={!!form.is_public}
                  onChange={(e) => setForm(prev => ({ ...prev, is_public: e.target.checked }))}
                />
              }
              label="Modelo público"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={!!form.is_active}
                  onChange={(e) => setForm(prev => ({ ...prev, is_active: e.target.checked }))}
                />
              }
              label="Activo"
            />

            <TextField
              label="Versión"
              value={form.version_id}
              onChange={(e) => setForm(prev => ({ ...prev, version_id: e.target.value }))}
            />

            {isEdit && (
              <FormControlLabel
                control={
                  <Switch
                    checked={!!(form.is_deleted || form.soft_delete?.deleted)}
                    onChange={(e) => {
                      const deleted = e.target.checked;
                      setForm(prev => ({
                        ...prev,
                        is_deleted: deleted,
                        soft_delete: {
                          deleted,
                          deleted_at: deleted ? prev.soft_delete?.deleted_at || null : null,
                        },
                      }));
                    }}
                  />
                }
                label="Marcar como eliminado (soft)"
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForm(false)} disabled={saving}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!validate.valid || saving}
          >
            {saving ? "Guardando..." : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogo Soft Delete */}
      <Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
        <DialogTitle>Eliminar modelo (soft delete)</DialogTitle>
        <DialogContent dividers>
          <Typography>
            ¿Deseas marcar como eliminado el modelo{" "}
            <strong>{toDelete?.name}</strong>? Podrás verlo si activas “Ver eliminados“.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={confirmSoftDelete}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toasters */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="error" onClose={() => setError("")} variant="filled">
          {error}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="success" onClose={() => setSuccess("")} variant="filled">
          {success}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SemanticModels;
